import React, { useMemo, useState } from 'react';

import classNames from 'classnames';

import RadioRagMatrix from './RadioRagMatrix';
import { useRadioReportContext } from './RadioReportContext';
import RadioReportTableRow from './RadioReportTableRow';
import RadioWeightedValueSummary from './RadioWeightedValueSummary';

import { formatFigure } from 'Helpers/formatService';

import Button from 'CommonComponents/Button';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import FileExportIcon from 'Assets/icons/file-export.svg';

// Detailed value columns in render order (mirrors RadioReportDetailedSheet).
// Type + Description are always-present leading columns handled separately.
const DETAIL_COLUMNS = [
    { key: 'status', label: () => $t('Status') },
    { key: 'rag', label: () => $t('RAG') },
    { key: 'priority', label: () => $t('Priority') },
    { key: 'owner', label: () => $t('Owner') },
    { key: 'impact_category', label: () => $t('Impact Category') },
    { key: 'afc', label: () => $t('AFC') },
    { key: 'actions', label: () => $t('Actions') },
];

// The two leading columns (Type + Description) are always shown.
const LEADING_COLUMN_COUNT = 2;

/**
 * Resolve which detailed value columns are visible. When the criteria carry an
 * explicit visible_columns set, honour it; otherwise show every column.
 */
const resolveColumns = (visibleColumns) => {
    const all = !Array.isArray(visibleColumns) || visibleColumns.length === 0;
    return DETAIL_COLUMNS.reduce((acc, col) => {
        acc[col.key] = all || visibleColumns.includes(col.key);
        return acc;
    }, {});
};

const EmptyRow = ({ colSpan }) => (
    <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
        <td colSpan={colSpan} className="tw-p-4">
            <span>{$t('No data available')}</span>
        </td>
    </tr>
);

/**
 * Detailed table: group header rows ({ group, _is_group }) interleaved with item
 * rows. The styles.groups index map (1-based) classifies which rows are section
 * headers; clicking one collapses the items beneath it.
 */
const DetailedTable = ({ rows, styles, loading, visibleColumns }) => {
    const [collapsed, setCollapsed] = useState({});

    const columns = useMemo(() => resolveColumns(visibleColumns), [visibleColumns]);
    const visibleCount = DETAIL_COLUMNS.filter((col) => columns[col.key]).length;
    const columnCount = LEADING_COLUMN_COUNT + visibleCount;

    const groupIndices = useMemo(() => new Set(styles?.groups ?? []), [styles]);

    const toggleGroup = (groupLabel) => setCollapsed((prev) => ({ ...prev, [groupLabel]: !prev[groupLabel] }));

    let currentGroup = null;

    return (
        <table className="inbud-table inbud-table_first-sticky">
            <thead className="tw-sticky tw-top-0 tw-z-10">
                <tr className="*:!tw-bg-white *:!tw-align-middle *:tw-border-b-2 *:tw-border-r-2">
                    <th className="tw-z-10 tw-min-w-32">{$t('Type')}</th>
                    <th className="tw-w-1/3 tw-min-w-72">{$t('Description')}</th>
                    {DETAIL_COLUMNS.map(
                        (col) =>
                            columns[col.key] && (
                                <th key={col.key} className="tw-min-w-32">
                                    {col.label()}
                                </th>
                            ),
                    )}
                </tr>
            </thead>
            <tbody>
                {loading && <TableRowSkeleton rows={10} columns={columnCount} />}
                {!loading && rows.length === 0 && <EmptyRow colSpan={columnCount} />}
                {!loading &&
                    rows.length > 0 &&
                    rows.map((row, idx) => {
                        if (Array.isArray(row)) return null;

                        const rowNumber = idx + 1;
                        const isGroup = row._is_group === true || groupIndices.has(rowNumber);

                        if (isGroup) {
                            currentGroup = row.group;
                            const isCollapsed = !!collapsed[currentGroup];

                            return (
                                <tr key={`group-${idx}`} className="tw-font-medium !tw-bg-charcoal-75 tw-cursor-pointer" onClick={() => toggleGroup(row.group)}>
                                    <td className="tw-text-white" colSpan={columnCount}>
                                        <div className="tw-flex tw-items-center tw-gap-x-1.5">
                                            {isCollapsed ? (
                                                <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-white" />
                                            ) : (
                                                <ChevronDownIcon className="tw-h-1.5 tw-w-1.5 tw-text-white" />
                                            )}
                                            <span>{row.group}</span>
                                        </div>
                                    </td>
                                </tr>
                            );
                        }

                        if (currentGroup !== null && collapsed[currentGroup]) {
                            return null;
                        }

                        return <RadioReportTableRow key={row.id ?? idx} row={row} columns={columns} />;
                    })}
            </tbody>
        </table>
    );
};

const SUMMARY_COLUMNS = [
    { key: 'total', label: () => $t('Total') },
    { key: 'risks', label: () => $t('Risks') },
    { key: 'issues', label: () => $t('Issues') },
    { key: 'decisions', label: () => $t('Decisions') },
    { key: 'assumptions', label: () => $t('Assumptions') },
    { key: 'opportunities', label: () => $t('Opportunities') },
];

/**
 * Summary table: one aggregated row per group with a total and a per-type
 * breakdown.
 */
const SummaryTable = ({ rows, loading }) => {
    const columnCount = 1 + SUMMARY_COLUMNS.length;

    return (
        <table className="inbud-table inbud-table_first-sticky">
            <thead className="tw-sticky tw-top-0 tw-z-10">
                <tr className="*:!tw-bg-white *:!tw-align-middle *:tw-border-b-2 *:tw-border-r-2">
                    <th className="tw-z-10 tw-w-1/4 tw-min-w-56">{$t('Group')}</th>
                    {SUMMARY_COLUMNS.map((col) => (
                        <th key={col.key} className="tw-min-w-24">
                            {col.label()}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {loading && <TableRowSkeleton rows={10} columns={columnCount} />}
                {!loading && rows.length === 0 && <EmptyRow colSpan={columnCount} />}
                {!loading &&
                    rows.length > 0 &&
                    rows.map((row, idx) => (
                        <tr key={idx} className="tw-text-xs !tw-bg-white">
                            <td className="!tw-bg-charcoal-15 tw-text-charcoal">{row.group}</td>
                            {SUMMARY_COLUMNS.map((col) => (
                                <td key={col.key} className="inbud-numeric-cell">
                                    {formatFigure(row[col.key] ?? 0, String)}
                                </td>
                            ))}
                        </tr>
                    ))}
            </tbody>
        </table>
    );
};

/**
 * The RADIO report table — summary or detailed, with a toolbar that triggers the
 * async Excel export.
 */
const RadioReportTable = () => {
    const { report, loading, criteria, exportReport, exporting } = useRadioReportContext();

    const format = criteria?.format ?? 'register';
    const isSummary = (criteria?.detail_level ?? 'detailed') === 'summary';
    const rows = report?.rows ?? [];
    const styles = report?.styles ?? {};

    // Register data drives the empty-state disable; the format views carry their
    // own empty states, so enable the export whenever a payload is loaded.
    const hasData = format === 'register' ? rows.length > 0 : !!report;

    let body;
    if (format === 'rag_matrix') {
        body = <RadioRagMatrix data={report?.rag_matrix} loading={loading} />;
    } else if (format === 'weighted_value') {
        body = <RadioWeightedValueSummary data={report?.weighted_value} loading={loading} />;
    } else if (isSummary) {
        body = <SummaryTable rows={rows} loading={loading} />;
    } else {
        body = <DetailedTable rows={rows} styles={styles} loading={loading} visibleColumns={criteria?.visible_columns} />;
    }

    return (
        <div className="tw-flex-auto tw-flex tw-flex-col tw-gap-y-4">
            <div className="tw-flex tw-justify-end">
                <Button
                    variant="primary"
                    type="button"
                    onClick={() => exportReport().catch(() => {})}
                    disabled={!hasData || loading || exporting}
                    loading={exporting}
                    className={classNames({ 'disabled:tw-bg-blue-25': true })}
                >
                    <FileExportIcon className="tw-h-4 tw-w-4 tw-me-1.5" />
                    {$t('Download to Excel')}
                </Button>
            </div>

            <div className="tw-flex-auto">
                <div className="inbud-table_wrapper tw-h-full tw-overflow-auto">{body}</div>
            </div>
        </div>
    );
};

export default RadioReportTable;
